import { AccountCircle, Logout } from "@mui/icons-material"
import {
	Avatar,
	Button,
	ClickAwayListener,
	Grid,
	Grow,
	Link,
	MenuItem,
	Paper,
	Popper,
} from "@mui/material"
import { useTheme } from "@mui/material/styles"
import { FC, MouseEvent, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { AppDispatch } from "../../store"
import { logout } from "../../store/actions/auth"
import { RootState } from "../../store/reducers"
import { detectImageType } from "../../utils/checkImageType"
import { getBestContrastColor } from "../../utils/contrast-color"
import Profile from "../profile"

const UserMenu: FC = () => {
	const dispatch = useDispatch<AppDispatch>()
	const authState = useSelector((state: RootState) => state.auth)
	const [anchorElement, setAnchorElement] = useState<undefined | HTMLElement>(
		undefined
	)
	const [isProfileDrawerOpen, setIsProfileDrawerOpen] = useState<boolean>(false)
	const [open, setOpen] = useState<boolean>(false)
	const theme = useTheme()
	const { t } = useTranslation(["userMenu"])

	const handleClick = (event: MouseEvent<HTMLElement>) => {
		setAnchorElement(event.currentTarget)
		setOpen((previous) => !previous)
	}

	const handleClose = () => {
		setAnchorElement(undefined)
		setOpen(false)
	}

	const handleLogout = () => {
		dispatch(logout())
		handleClose()
	}

	const getImageSrc = (raw: string | undefined | null) => {
		if (!raw) return undefined

		// Se contiene già "data:image", è una data URL valida
		if (raw.startsWith("data:image")) {
			return raw
		}

		// Prova a capire che tipo di immagine è
		const firstChars = raw.slice(0, 20)

		if (firstChars.includes("<svg")) {
			// SVG XML inline (non base64), bisogna convertirlo in data URI
			return `data:image/svg+xml;utf8,${encodeURIComponent(raw)}`
		}

		const base64Pure = btoa(raw)
		const imageType = detectImageType(base64Pure)
		// Altrimenti assumiamo sia una stringa base64 pura → aggiungiamo intestazione generica PNG (o deduci meglio se serve)
		return `data:${imageType};base64,${base64Pure}`
	}

	const base64Image = authState?.user?.avatar ?? undefined
	const userAvatarSrc = base64Image && atob(base64Image)

	return (
		<>
			<Button
				sx={{
					marginRight: 0,
					color: theme.palette.primary.main || "inherit",
				}}
				onClick={handleClick}
			>
				{userAvatarSrc ? (
					<Avatar
						src={getImageSrc(userAvatarSrc)}
						sx={{ width: "30px", height: "30px" }}
					/>
				) : (
					<AccountCircle sx={{ fontSize: "30px" }} />
				)}

				<div
					style={{
						textAlign: "left",
						color: getBestContrastColor(theme.palette.primary.contrastText),
						fontSize: "13px",
						marginLeft: "8px",
						textTransform: "capitalize",
						lineHeight: "11px",
					}}
				>
					{authState.user?.fullName} <br />
					<span
						style={{
							fontWeight: "600",
							fontSize: "11px",
							color: theme.palette.primary.main,
						}}
					>
						{authState.user?.roles?.[0]?.role}
					</span>
				</div>
			</Button>

			<Popper
				open={open}
				anchorEl={anchorElement}
				transition
				disablePortal
				placement="bottom"
			>
				{({ TransitionProps }) => (
					<Grow {...TransitionProps}>
						<Paper
							sx={{
								marginLeft: "-60px",
								marginRight: "24px",
								backgroundColor: `${theme.palette.primary.contrastText}`,
								color: `${getBestContrastColor(theme.palette.primary.contrastText)}`,
							}}
						>
							<div
								style={{
									display: "flex",
									justifyContent: "center",
									marginLeft: "55px",
								}}
							>
								<div
									style={{
										width: 0,
										height: 0,
										borderLeft: "6px solid transparent",
										borderRight: "6px solid transparent",
										borderBottom: `6px solid ${theme.palette.primary.main}`,
										marginTop: "-6px",
									}}
								/>
							</div>
							<Grid
								container
								alignItems={"flex-start"}
								justifyContent={"space-between"}
								style={{ padding: "16px" }}
							>
								<Grid item>
									{userAvatarSrc ? (
										<Avatar
											src={getImageSrc(userAvatarSrc)}
											sx={{ width: "40px", height: "40px" }}
										/>
									) : (
										<AccountCircle
											sx={{
												fontSize: "40px",
												color: theme.palette.primary.main,
											}}
										/>
									)}
								</Grid>
								<Grid item>
									<div
										style={{
											color: getBestContrastColor(
												theme.palette.primary.contrastText
											),
											fontSize: "13px",
											marginLeft: "8px",
											textAlign: "left",
										}}
									>
										{authState.user?.fullName} <br /> {authState.user?.email}
									</div>
									<div>
										<Link
											component="button"
											variant="caption"
											underline="none"
											onClick={() => {
												setIsProfileDrawerOpen(!isProfileDrawerOpen)
											}}
										>
											{t("profile.title")}
										</Link>
									</div>
								</Grid>
							</Grid>
							<ClickAwayListener onClickAway={handleClose}>
								<div>
									<MenuItem onClick={handleLogout}>
										<Logout />{" "}
										<span style={{ marginLeft: "8px" }}>{t("logout")}</span>
									</MenuItem>
								</div>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
			<Profile
				isDrawerOpen={isProfileDrawerOpen}
				handleClose={() => setIsProfileDrawerOpen(false)}
			/>
		</>
	)
}

export default UserMenu
